import {createContext, ReactNode, useContext, useRef, useState} from 'react';
import {useMessages,} from 'react-chatbotify';
import {FormattedMessage} from 'react-intl';
import {useSubmitChatHistory} from '../components/hooks';

type ButtonSendChatHistoryContextType = {
  toggleQuestion: () => Promise<void>;
  isDisabled: boolean;
  enableSendEmailButton: () => void;
  disableSendEmailButton: () => void;
}

interface UserContextProviderProps {
  children: ReactNode;
}

export const ButtonSendChatHistoryContext = createContext<ButtonSendChatHistoryContextType>({
  toggleQuestion: async () => {},
  isDisabled: true,
  enableSendEmailButton: () => {},
  disableSendEmailButton: () => {},
});

export const useButtonSendChatHistoryContext = () => useContext(ButtonSendChatHistoryContext);

export const ButtonSendChatHistoryContextProvider = ({children}: UserContextProviderProps) => {
  const chat = useMessages();
  const [msgID, setMsgID] = useState('');
  const [loading, setLoading] = useState(false);
  const [disabled, setDisabled] = useState(true);
  const ref = useRef<HTMLDivElement>(null);
  const {submitChatHistory} = useSubmitChatHistory();

  const submitHistory = async () => {
    try {
      setLoading(true);
      await submitChatHistory();
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
      setDisabled(true);
    }
  };

  const toggleQuestion = async () => {
    if (msgID) {
      await removeMsg();
    } else {
      await createMsg();
    }
  };

  const createMsg = async () => {
    const message = await chat.injectMessage(SendHistoryMessage);
    if (message) {
      setMsgID(message.id);
    }
  };

  const onConfirm = async () => {
    await submitHistory();
    await removeMsg();
  };
  const onCancel = async () => {
    await removeMsg();
  };

  const removeMsg = async () => {
    if (msgID) {
      await chat.removeMessage(msgID);
      setMsgID('');
    }
    if (ref.current) {
      ref.current.remove();
    }
  };

  const enableButton = () => {
    setDisabled(false);
  };

  const disableButton = () => {
    setDisabled(true);
  };

  const SendHistoryMessage = (
    <div className="chat-message" ref={ref}>
      <div className="mb-1">
        <FormattedMessage
          id="popup-send-history-question"
          defaultMessage="Should I send you a summary of our correspondence?"
        />
      </div>
      <div className="d-flex justify-content-center pb-3">
        <button type="button" className="btn mx-1 btn-primary text-white" onClick={() => onConfirm()}>
          {loading
            ? (
              <div className="spinner-border text-white" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
            )
            : (
              <FormattedMessage
                id="popup-send-history-yes"
                defaultMessage="I would be happy, thank you."
              />
            )}
        </button>
        <button type="button" className="btn mx-1 btn-danger text-white" onClick={() => onCancel()}>
          <FormattedMessage
            id="popup-send-history-no"
            defaultMessage="No need"
          />
        </button>
      </div>
    </div>
  );

  return (
    <ButtonSendChatHistoryContext.Provider value={{toggleQuestion, isDisabled: disabled, enableSendEmailButton: enableButton, disableSendEmailButton: disableButton}}>
      {children}
    </ButtonSendChatHistoryContext.Provider>
  );
};